import ShelfController from '../../core/shelf.controller';
import Constants from '../../data/constants';
import { can, isAuditAnalyst } from '../../core/access';

class AuditConsoleController extends ShelfController {
    state = {
        tabKey: 'summary',
        analytics: null,
        analyticsLoading: true,
        products: [],
        productsLoading: false,
        productsPagination: { page: 1, limit: 25, total: 0, pages: 1 },
        productSearch: '',
        productStatusFilter: undefined,
        productReviewStatusFilter: undefined,
        productMissingFilter: undefined,
        productDuplicatesFilter: false,
        productInvalidCodesFilter: false,
        productShowArchived: false,
        productCategoryFilter: undefined,
        productSegmentFilter: undefined,
        productCountryFilter: undefined,
        productGramsMin: undefined,
        productGramsMax: undefined,
        productPriceMin: undefined,
        productPriceMax: undefined,
        productFacets: { categories: [], segments: [], countries: [] },
        showDuplicatesPanel: false,
        duplicateGroups: [],
        duplicatesLoading: false,
        records: [],
        recordsLoading: false,
        recordsPagination: { page: 1, limit: 25, total: 0, pages: 1 },
        recordCodeFilter: '',
        recordAuditorFilter: undefined,
        recordUserTypeFilter: undefined,
        auditorsList: [],
        storeChecks: [],
        storeChecksLoading: false,
        storeChecksPagination: { page: 1, limit: 25, total: 0, pages: 1 },
        productDrawerOpen: false,
        selectedAuditProduct: null,
        savingProduct: false,
        recordDrawerOpen: false,
        selectedRecordId: null,
        storeCheckDrawerOpen: false,
        selectedStoreCheckId: null,
        auditorDrawerOpen: false,
        selectedAuditorId: null,
        reviewQueue: [],
        reviewQueueLoading: false,
        reviewQueuePagination: { page: 1, limit: 20, total: 0, pages: 1 },
        reviewSelectedIds: [],
        reviewFocusIndex: 0,
        mergeModalOpen: false,
        mergeGroup: null,
        merging: false,
        bulkLoading: false,
        bulkCategory: '',
        bulkSegment: '',
        selectedRecordIds: [],
        selectedProductIds: [],
        deleting: false,
        correctingCurrency: false,
    };

    // Análisis Auditor only gets the "Visitas por tienda" / "Registros (escaneos)" tabs, with the
    // scanning-user filter pinned to auditors (also enforced server-side).
    get isAuditAnalyst() {
        return isAuditAnalyst(this.user?.userType);
    }

    // "Gestionar auditoría": edits, deletes and console analytics. Without it the console is read-only.
    get canManageAudit() {
        return !this.isAuditAnalyst && can(this.user?.userType, 'audit.manage');
    }

    static ANALYST_TABS = ['checks', 'records'];

    _allowedTab(tab) {
        if (!this.isAuditAnalyst) return tab || 'summary';
        return AuditConsoleController.ANALYST_TABS.includes(tab) ? tab : 'checks';
    }

    _syncUrlParams() {
        const params = new URLSearchParams(this.props.location?.search || '');
        const tab = this._allowedTab(params.get('tab'));
        const updates = { tabKey: tab };
        if (this.isAuditAnalyst) updates.recordUserTypeFilter = 'auditor';
        if (params.get('code')) updates.recordCodeFilter = params.get('code');
        if (params.get('recordAuditor')) updates.recordAuditorFilter = params.get('recordAuditor');
        if (params.get('recordUserType')) updates.recordUserTypeFilter = params.get('recordUserType');
        if (params.get('search')) updates.productSearch = params.get('search');
        if (params.get('auditor')) updates.selectedAuditorId = params.get('auditor');
        this.setState(updates, () => {
            if (params.get('auditor')) this.setState({ auditorDrawerOpen: true });
            this._loadTabData(tab);
        });
    }

    _updateUrlParams(patch = {}) {
        const params = new URLSearchParams(this.props.location?.search || '');
        Object.entries(patch).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') params.delete(k);
            else params.set(k, String(v));
        });
        const search = params.toString();
        this.props.navigate?.({ search: search ? `?${search}` : '' }, { replace: true });
    }

    _loadTabData(tabKey) {
        if (tabKey === 'products') {
            if (!this.state.productsLoading) this.loadProducts(1);
            if (!this.state.productFacets?.categories?.length) this.loadProductFacets();
        }
        if (tabKey === 'records') {
            if (!this.state.auditorsList.length) this.loadAuditors();
            if (!this.state.recordsLoading) this.loadRecords(1);
        }
        if (tabKey === 'checks' && !this.state.storeChecksLoading) this.loadStoreChecks(1);
        if (tabKey === 'review' && !this.state.reviewQueueLoading) this.loadReviewQueue(1);
        if (tabKey === 'auditors' && !this.state.analytics) this.loadAnalytics();
    }

    async initialize() {
        if (!this.isAuthenticated) {
            this.navigateLogin();
            return;
        }
        this._syncUrlParams();
        if (this.canManageAudit) await this.loadAnalytics(); // analytics endpoint needs "Gestionar auditoría"
        this._keyboardHandler = (e) => this._handleKeyboard(e);
        window.addEventListener('keydown', this._keyboardHandler);
    }

    componentWillUnmount() {
        if (this._keyboardHandler) window.removeEventListener('keydown', this._keyboardHandler);
    }

    _handleKeyboard(e) {
        if (this.state.tabKey !== 'review') return;
        const tag = e.target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        const items = this.state.reviewQueue;
        if (!items.length) return;
        const idx = this.state.reviewFocusIndex;
        if (e.key === 'j') {
            e.preventDefault();
            this.setState({ reviewFocusIndex: Math.min(idx + 1, items.length - 1) });
        } else if (e.key === 'k') {
            e.preventDefault();
            this.setState({ reviewFocusIndex: Math.max(idx - 1, 0) });
        } else if (e.key === 'a') {
            e.preventDefault();
            const item = items[idx];
            if (item?.product) this.approveReviewItem(item.product);
        } else if (e.key === 'r') {
            e.preventDefault();
            const item = items[idx];
            if (item?.product) this.rejectReviewItem(item.product);
        } else if (e.key === 'e') {
            e.preventDefault();
            const item = items[idx];
            if (item?.product) this.openProductDrawer(item.product);
        }
    }

    setTabKey = (key) => {
        const tabKey = this._allowedTab(key);
        this._updateUrlParams({ tab: tabKey });
        this.setState({ tabKey }, () => this._loadTabData(tabKey));
    };

    async loadAnalytics() {
        this.setState({ analyticsLoading: true });
        try {
            const res = await this.data.fetchAuditAdminAnalytics();
            if (res?.success) {
                this.setState({ analytics: res.data, analyticsLoading: false });
            } else {
                this.props.onMessage?.('error', res?.message || 'Error al cargar analíticas');
                this.setState({ analyticsLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al cargar analíticas');
            this.setState({ analyticsLoading: false });
        }
    }

    async loadProducts(page = 1) {
        this.setState({ productsLoading: true });
        try {
            const {
                productSearch, productStatusFilter, productReviewStatusFilter, productMissingFilter,
                productDuplicatesFilter, productInvalidCodesFilter, productShowArchived,
                productCategoryFilter, productSegmentFilter, productCountryFilter,
                productGramsMin, productGramsMax, productPriceMin, productPriceMax, productsPagination,
            } = this.state;
            const res = await this.data.fetchAuditAdminAuditProducts({
                page,
                limit: productsPagination.limit || 25,
                search: productSearch || undefined,
                status: productStatusFilter,
                reviewStatus: productReviewStatusFilter,
                missingData: productMissingFilter,
                duplicatesOnly: productDuplicatesFilter ? 'true' : undefined,
                invalidCodesOnly: productInvalidCodesFilter ? 'true' : undefined,
                includeArchived: productShowArchived ? 'true' : undefined,
                category: productCategoryFilter,
                segment: productSegmentFilter,
                country: productCountryFilter,
                gramsMin: productGramsMin ?? undefined,
                gramsMax: productGramsMax ?? undefined,
                priceMin: productPriceMin ?? undefined,
                priceMax: productPriceMax ?? undefined,
            });
            if (res?.success) {
                this.setState({
                    products: res.data?.products || [],
                    productsPagination: res.data?.pagination || {},
                    productsLoading: false,
                });
            } else {
                this.setState({ productsLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al cargar productos');
            this.setState({ productsLoading: false });
        }
    }

    async loadDuplicates() {
        this.setState({ duplicatesLoading: true, showDuplicatesPanel: true });
        try {
            const res = await this.data.fetchAuditAdminProductDuplicates({ strategy: 'both' });
            if (res?.success) {
                this.setState({ duplicateGroups: res.data?.groups || [], duplicatesLoading: false });
            } else {
                this.setState({ duplicatesLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al cargar duplicados');
            this.setState({ duplicatesLoading: false });
        }
    }

    async loadAuditors() {
        try {
            const types = this.isAuditAnalyst
                ? [{ type: Constants.USER_TYPES.AUDITOR, label: 'Auditor' }]
                : [
                      { type: Constants.USER_TYPES.AUDITOR, label: 'Auditor' },
                      { type: Constants.USER_TYPES.PROMOTOR, label: 'Promotor' },
                      { type: Constants.USER_TYPES.ASESOR, label: 'Asesor' },
                  ];
            const results = await Promise.all(types.map((t) => this.data.fetchUsersByUserType(t.type).catch(() => [])));
            const auditorsList = [];
            results.forEach((users, i) => {
                if (Array.isArray(users)) {
                    users.forEach((u) => auditorsList.push({ ...u, roleLabel: types[i].label }));
                }
            });
            this.setState({ auditorsList });
        } catch (e) {
            this.props.onMessage?.('error', 'No se pudo cargar la lista de usuarios');
        }
    }

    async loadRecords(page = 1) {
        this.setState({ recordsLoading: true });
        try {
            const { recordCodeFilter, recordAuditorFilter, recordUserTypeFilter, recordsPagination } = this.state;
            const res = await this.data.fetchAuditAdminAuditRecords({
                page,
                limit: recordsPagination.limit || 25,
                code: recordCodeFilter || undefined,
                userId: recordAuditorFilter || undefined,
                userType: this.isAuditAnalyst ? 'auditor' : recordUserTypeFilter || undefined,
            });
            if (res?.success) {
                this.setState({
                    records: res.data?.records || [],
                    recordsPagination: res.data?.pagination || {},
                    recordsLoading: false,
                });
            } else {
                this.setState({ recordsLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al cargar registros');
            this.setState({ recordsLoading: false });
        }
    }

    async loadStoreChecks(page = 1) {
        this.setState({ storeChecksLoading: true });
        try {
            const { storeChecksPagination } = this.state;
            const res = await this.data.fetchAuditAdminStoreChecks({
                page,
                limit: storeChecksPagination.limit || 25,
                userType: this.isAuditAnalyst ? 'auditor' : undefined,
            });
            if (res?.success) {
                this.setState({
                    storeChecks: res.data?.storeChecks || [],
                    storeChecksPagination: res.data?.pagination || {},
                    storeChecksLoading: false,
                });
            } else {
                this.setState({ storeChecksLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al cargar visitas');
            this.setState({ storeChecksLoading: false });
        }
    }

    async loadReviewQueue(page = 1) {
        this.setState({ reviewQueueLoading: true });
        try {
            const { reviewQueuePagination } = this.state;
            const res = await this.data.fetchAuditAdminReviewQueue({
                page,
                limit: reviewQueuePagination.limit || 20,
                sort: 'oldest',
            });
            if (res?.success) {
                this.setState({
                    reviewQueue: res.data?.items || [],
                    reviewQueuePagination: res.data?.pagination || {},
                    reviewQueueLoading: false,
                    reviewFocusIndex: 0,
                });
            } else {
                this.setState({ reviewQueueLoading: false });
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al cargar cola de revisión');
            this.setState({ reviewQueueLoading: false });
        }
    }

    setProductSearch = (productSearch) => {
        this.setState({ productSearch });
        this._updateUrlParams({ search: productSearch });
    };
    setProductStatusFilter = (productStatusFilter) => this.setState({ productStatusFilter });
    setProductReviewStatusFilter = (productReviewStatusFilter) => this.setState({ productReviewStatusFilter }, () => this.loadProducts(1));
    setProductMissingFilter = (productMissingFilter) => this.setState({ productMissingFilter });
    setProductDuplicatesFilter = (productDuplicatesFilter) => this.setState({ productDuplicatesFilter }, () => this.loadProducts(1));
    setProductInvalidCodesFilter = (productInvalidCodesFilter) => this.setState({ productInvalidCodesFilter }, () => this.loadProducts(1));
    setProductShowArchived = (productShowArchived) => this.setState({ productShowArchived }, () => this.loadProducts(1));
    setProductCategoryFilter = (productCategoryFilter) => this.setState({ productCategoryFilter }, () => this.loadProducts(1));
    setProductSegmentFilter = (productSegmentFilter) => this.setState({ productSegmentFilter }, () => this.loadProducts(1));
    setProductCountryFilter = (productCountryFilter) => this.setState({ productCountryFilter }, () => this.loadProducts(1));
    // Range inputs don't auto-reload on every keystroke; they apply on Enter / "Buscar".
    setProductGramsMin = (productGramsMin) => this.setState({ productGramsMin });
    setProductGramsMax = (productGramsMax) => this.setState({ productGramsMax });
    setProductPriceMin = (productPriceMin) => this.setState({ productPriceMin });
    setProductPriceMax = (productPriceMax) => this.setState({ productPriceMax });

    clearProductFilters = () =>
        this.setState(
            {
                productSearch: '',
                productStatusFilter: undefined,
                productReviewStatusFilter: undefined,
                productMissingFilter: undefined,
                productDuplicatesFilter: false,
                productInvalidCodesFilter: false,
                productShowArchived: false,
                productCategoryFilter: undefined,
                productSegmentFilter: undefined,
                productCountryFilter: undefined,
                productGramsMin: undefined,
                productGramsMax: undefined,
                productPriceMin: undefined,
                productPriceMax: undefined,
            },
            () => {
                this._updateUrlParams({ search: '' });
                this.loadProducts(1);
            }
        );

    async loadProductFacets() {
        try {
            const res = await this.data.fetchAuditAdminAuditProductFacets();
            if (res?.success && res.data) {
                this.setState({
                    productFacets: {
                        categories: res.data.categories || [],
                        segments: res.data.segments || [],
                        countries: res.data.countries || [],
                    },
                });
            }
        } catch (e) {
            // Non-fatal: filters still work as free-form text on the backend.
        }
    }
    setRecordCodeFilter = (recordCodeFilter) => {
        this.setState({ recordCodeFilter });
        this._updateUrlParams({ code: recordCodeFilter });
    };
    setRecordAuditorFilter = (recordAuditorFilter) => {
        this.setState({ recordAuditorFilter }, () => this.loadRecords(1));
        this._updateUrlParams({ recordAuditor: recordAuditorFilter });
    };
    setRecordUserTypeFilter = (recordUserTypeFilter) => {
        if (this.isAuditAnalyst) return; // pinned to auditor
        this.setState({ recordUserTypeFilter }, () => this.loadRecords(1));
        this._updateUrlParams({ recordUserType: recordUserTypeFilter });
    };

    // Table pagination: keep the chosen page size, then reload that page.
    changeProductsPage = (page, pageSize) => {
        this.setState({ productsPagination: { ...this.state.productsPagination, page, limit: pageSize } }, () => this.loadProducts(page));
    };
    changeRecordsPage = (page, pageSize) => {
        this.setState({ recordsPagination: { ...this.state.recordsPagination, page, limit: pageSize } }, () => this.loadRecords(page));
    };
    changeStoreChecksPage = (page, pageSize) => {
        this.setState({ storeChecksPagination: { ...this.state.storeChecksPagination, page, limit: pageSize } }, () => this.loadStoreChecks(page));
    };

    hideDuplicatesPanel = () => this.setState({ showDuplicatesPanel: false });
    setBulkCategory = (bulkCategory) => this.setState({ bulkCategory });
    setBulkSegment = (bulkSegment) => this.setState({ bulkSegment });

    openProductDrawer = async (row) => {
        if (!row?._id) return;
        this.setState({ productDrawerOpen: true, selectedAuditProduct: { ...row } });
        try {
            const res = await this.data.fetchAuditAdminAuditProductById(row._id);
            if (res?.success && res.data) {
                this.setState({ selectedAuditProduct: res.data });
            }
        } catch (e) {
            this.props.onMessage?.('error', 'No se pudo cargar el detalle');
        }
    };

    closeProductDrawer = () => {
        this.setState({ productDrawerOpen: false, selectedAuditProduct: null });
    };

    openRecordDrawer = (record) => {
        const id = record?._id || record;
        this.setState({ recordDrawerOpen: true, selectedRecordId: id });
    };

    closeRecordDrawer = () => {
        this.setState({ recordDrawerOpen: false, selectedRecordId: null });
    };

    fetchRecordById = async (id) => {
        const res = await this.data.fetchAuditAdminAuditRecordById(id);
        return res?.success ? res.data : null;
    };

    openStoreCheckDrawer = (row) => {
        this.setState({ storeCheckDrawerOpen: true, selectedStoreCheckId: row._id });
    };

    closeStoreCheckDrawer = () => {
        this.setState({ storeCheckDrawerOpen: false, selectedStoreCheckId: null });
    };

    fetchStoreCheckRecords = async (id) => {
        const res = await this.data.fetchAuditAdminStoreCheckRecords(id);
        return res?.success ? res.data : null;
    };

    openAuditorDrawer = (userId) => {
        this._updateUrlParams({ auditor: userId });
        this.setState({ auditorDrawerOpen: true, selectedAuditorId: userId });
    };

    closeAuditorDrawer = () => {
        this._updateUrlParams({ auditor: '' });
        this.setState({ auditorDrawerOpen: false, selectedAuditorId: null });
    };

    fetchAuditorStats = async (userId) => {
        const res = await this.data.fetchAuditAdminAuditorStats(userId, { period: '30d' });
        return res?.success ? res.data : null;
    };

    fetchAuditorActivity = async (userId) => {
        const res = await this.data.fetchAuditAdminAuditorActivity(userId, { page: 1, limit: 15 });
        return res?.success ? res.data : null;
    };

    openMergeModal = (group) => {
        this.setState({ mergeModalOpen: true, mergeGroup: group });
    };

    closeMergeModal = () => {
        this.setState({ mergeModalOpen: false, mergeGroup: null });
    };

    async mergeProducts({ canonicalId, duplicateIds }) {
        this.setState({ merging: true });
        try {
            const res = await this.data.mergeAuditAdminProducts({ canonicalId, duplicateIds });
            if (res?.success) {
                this.props.onMessage?.('success', `Fusionados ${res.data?.mergedCount || 0} productos`);
                this.closeMergeModal();
                await this.loadDuplicates();
                await this.loadProducts(this.state.productsPagination.page || 1);
            } else {
                this.props.onMessage?.('error', res?.message || 'Error al fusionar');
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al fusionar');
        } finally {
            this.setState({ merging: false });
        }
    }

    async markAsDuplicate(product) {
        try {
            await this.data.bulkUpdateAuditAdminProducts({
                ids: [product._id],
                updates: { duplicateStatus: 'suspected' },
            });
            this.props.onMessage?.('success', 'Marcado como posible duplicado');
            await this.loadDuplicates();
        } catch (e) {
            this.props.onMessage?.('error', 'Error al marcar');
        }
    }

    toggleReviewSelection = (id, checked) => {
        this.setState((prev) => ({
            reviewSelectedIds: checked ? [...prev.reviewSelectedIds, id] : prev.reviewSelectedIds.filter((x) => x !== id),
        }));
    };

    async bulkApproveSelected() {
        const { reviewSelectedIds } = this.state;
        if (!reviewSelectedIds.length) return;
        await this._bulkProductUpdate(reviewSelectedIds, { status: 'complete', missingData: false, reviewStatus: 'approved' });
    }

    async bulkKeepPending(note) {
        const { reviewSelectedIds } = this.state;
        if (!reviewSelectedIds.length) return;
        await this._bulkProductUpdate(reviewSelectedIds, { reviewStatus: 'needs_fix', reviewNotes: note || 'Revisión pendiente' });
    }

    async bulkAssignTaxonomy(category, segment) {
        const { reviewSelectedIds } = this.state;
        if (!reviewSelectedIds.length) return;
        const updates = {};
        if (category) updates.category = category;
        if (segment) updates.segment = segment;
        if (!Object.keys(updates).length) return;
        await this._bulkProductUpdate(reviewSelectedIds, updates);
    }

    async approveReviewItem(product) {
        if (!product?._id) return;
        await this._bulkProductUpdate([product._id], { status: 'complete', missingData: false, reviewStatus: 'approved' });
    }

    async rejectReviewItem(product) {
        if (!product?._id) return;
        // Rejected products are archived (active: false) so they leave catalog totals.
        await this._bulkProductUpdate([product._id], { reviewStatus: 'rejected', active: false });
    }

    async bulkRejectSelected() {
        const { reviewSelectedIds } = this.state;
        if (!reviewSelectedIds.length) return;
        await this._bulkProductUpdate(reviewSelectedIds, { reviewStatus: 'rejected', active: false });
    }

    async _bulkProductUpdate(ids, updates) {
        this.setState({ bulkLoading: true });
        try {
            const res = await this.data.bulkUpdateAuditAdminProducts({ ids, updates });
            if (res?.success) {
                this.props.onMessage?.('success', `Actualizados ${res.data?.modified || 0} productos`);
                this.setState({ reviewSelectedIds: [] });
                await this.loadReviewQueue(this.state.reviewQueuePagination.page || 1);
                await this.loadProducts(this.state.productsPagination.page || 1);
                await this.loadAnalytics();
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error en actualización masiva');
        } finally {
            this.setState({ bulkLoading: false });
        }
    }

    exportReviewCsv() {
        const { reviewQueue, reviewSelectedIds } = this.state;
        const items = reviewSelectedIds.length
            ? reviewQueue.filter((i) => reviewSelectedIds.includes(i.product?._id))
            : reviewQueue;
        const rows = [['nombre', 'codigo', 'categoria', 'segmento', 'estado', 'fecha_escaneo']];
        items.forEach((i) => {
            rows.push([
                i.product?.name || '',
                i.product?.code || '',
                i.product?.category || '',
                i.product?.segment || '',
                i.product?.status || '',
                i.latestScan?.createdAt || '',
            ]);
        });
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'cola-revision-auditoria.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    async saveAuditProduct(values) {
        const { selectedAuditProduct } = this.state;
        if (!selectedAuditProduct?._id) return;
        this.setState({ savingProduct: true });
        try {
            const payload = {
                name: values.name,
                code: values.code,
                segment: values.segment || undefined,
                category: values.category || undefined,
                country: values.country || undefined,
                grams: values.grams === '' || values.grams === undefined ? null : Number(values.grams),
                price: values.price === '' || values.price === undefined ? null : Number(values.price),
                currency: values.currency || undefined,
                active: values.active,
                missingData: values.missingData,
                status: values.status,
                enrichedAt: values.enrichedAt ? values.enrichedAt.toISOString?.() || values.enrichedAt : null,
                enrichedBy: values.enrichedBy || null,
                tenantId: values.tenantId || null,
            };
            const res = await this.data.updateAuditAdminAuditProduct(selectedAuditProduct._id, payload);
            if (res?.success) {
                this.props.onMessage?.('success', 'Producto de auditoría actualizado');
                this.closeProductDrawer();
                await this.loadProducts(this.state.productsPagination.page || 1);
                await this.loadAnalytics();
            } else {
                this.props.onMessage?.('error', res?.message || 'Error al guardar');
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al guardar');
        } finally {
            this.setState({ savingProduct: false });
        }
    }

    navigateKpiFilter = (tab, extra = {}) => {
        this._updateUrlParams({ tab, ...extra });
        this.setState({ tabKey: tab, ...extra }, () => this._loadTabData(tab));
    };

    toggleRecordSelection = (ids) => {
        this.setState({ selectedRecordIds: ids });
    };

    toggleProductSelection = (ids) => {
        this.setState({ selectedProductIds: ids });
    };

    async _deleteRecord(id) {
        this.setState({ deleting: true });
        try {
            const res = await this.data.deleteAuditAdminAuditRecord(id);
            if (res?.success) {
                this.props.onMessage?.('success', 'Escaneo eliminado');
                this.setState({ recordDrawerOpen: false, selectedRecordId: null, selectedRecordIds: [] });
                await Promise.all([this.loadRecords(this.state.recordsPagination.page || 1), this.loadAnalytics()]);
            } else {
                this.props.onMessage?.('error', res?.message || 'Error al eliminar');
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al eliminar escaneo');
        } finally {
            this.setState({ deleting: false });
        }
    }

    async _bulkDeleteRecords(ids) {
        this.setState({ deleting: true });
        try {
            const res = await this.data.bulkDeleteAuditAdminAuditRecords(ids);
            if (res?.success) {
                this.props.onMessage?.('success', `Eliminados ${res.data?.deletedCount || 0} escaneos`);
                this.setState({ selectedRecordIds: [] });
                await Promise.all([this.loadRecords(this.state.recordsPagination.page || 1), this.loadAnalytics()]);
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al eliminar escaneos');
        } finally {
            this.setState({ deleting: false });
        }
    }

    async _bulkCorrectCurrency(ids, currency) {
        if (!ids?.length) return;
        this.setState({ correctingCurrency: true });
        try {
            const res = await this.data.bulkCurrencyAuditAdminAuditRecords(ids, currency);
            if (res?.success) {
                const label = currency === 'bs' ? 'bolívares' : 'dólares';
                this.props.onMessage?.('success', `Moneda corregida a ${label} en ${res.data?.modified || 0} escaneo(s)`);
                this.setState({ selectedRecordIds: [] });
                await Promise.all([this.loadRecords(this.state.recordsPagination.page || 1), this.loadAnalytics()]);
            } else {
                this.props.onMessage?.('error', res?.message || 'No se pudo corregir la moneda');
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al corregir moneda');
        } finally {
            this.setState({ correctingCurrency: false });
        }
    }

    async _deleteStoreCheck(id) {
        this.setState({ deleting: true });
        try {
            const res = await this.data.deleteAuditAdminStoreCheck(id);
            if (res?.success) {
                this.props.onMessage?.('success', `Visita eliminada (${res.data?.deletedScans || 0} escaneos)`);
                this.setState({ storeCheckDrawerOpen: false, selectedStoreCheckId: null });
                await Promise.all([this.loadStoreChecks(this.state.storeChecksPagination.page || 1), this.loadAnalytics()]);
            }
        } catch (e) {
            this.props.onMessage?.('error', 'Error al eliminar visita');
        } finally {
            this.setState({ deleting: false });
        }
    }

    async _deleteProduct(id, force = false) {
        this.setState({ deleting: true });
        try {
            const res = await this.data.deleteAuditAdminAuditProduct(id, force ? { force: 'true' } : {});
            if (res?.success) {
                const msg = res.data?.archived
                    ? 'Producto archivado (desactivado)'
                    : `Producto eliminado (${res.data?.unlinkedScans || 0} escaneos desvinculados)`;
                this.props.onMessage?.('success', msg);
                this.setState({ productDrawerOpen: false, selectedAuditProduct: null, selectedProductIds: [] });
                await Promise.all([this.loadProducts(this.state.productsPagination.page || 1), this.loadAnalytics()]);
            } else {
                this.props.onMessage?.('error', res?.message || 'Error al eliminar');
            }
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al eliminar producto');
        } finally {
            this.setState({ deleting: false });
        }
    }

    correctRecordCurrencyToBs = async (record) => {
        if (!record?._id) return null;
        this.setState({ correctingCurrency: true });
        try {
            const res = await this.data.patchAuditAdminAuditRecordCurrency(record._id, { currency: 'bs' });
            if (res?.success) {
                const usd = res.data?.priceUsd;
                const msg =
                    usd != null
                        ? `Moneda corregida a bolívares → $${Number(usd).toFixed(2)} USD`
                        : res.message || 'Moneda corregida a bolívares';
                this.props.onMessage?.('success', msg);
                await Promise.all([
                    this.loadRecords(this.state.recordsPagination.page || 1),
                    this.loadAnalytics(),
                ]);
                return res.data;
            }
            this.props.onMessage?.('error', res?.message || 'No se pudo corregir la moneda');
            return null;
        } catch (e) {
            this.props.onMessage?.('error', e?.response?.data?.message || e?.message || 'Error al corregir moneda');
            return null;
        } finally {
            this.setState({ correctingCurrency: false });
        }
    };
}

export default AuditConsoleController;
